import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog } from '@betareader/design-system';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getFreshIdToken } from '../../utils/authUtil';

export type FeedbackReportTarget = {
  kind: 'comment' | 'reply';
  contentId: string;
  userId: string;
  displayName: string;
};

type Props = {
  bookId: string;
  target: FeedbackReportTarget | null;
  onClose: () => void;
};

const FeedbackReportModal: React.FC<Props> = ({ bookId, target, onClose }) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setReason('');
    setDetails('');
    setError('');
    setBlocked(false);
  }, [target?.contentId]);

  const send = async (url: string, body?: object) => {
    const token = await getFreshIdToken();
    const response = await fetch(
      `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}${url}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'br-token': token },
        body: body ? JSON.stringify(body) : undefined,
      }
    );
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.msg || t('FailedToSubmitReport'));
    }
  };

  const act = async (action: 'report' | 'block') => {
    if (!target || busy) return;
    setBusy(true);
    setError('');
    try {
      if (action === 'report') {
        await send(`/books/${bookId}/feedback-reports`, {
          kind: target.kind,
          contentId: target.contentId,
          reason,
          comment: details,
        });
        toast.info(t('ReportSubmitted'));
        onClose();
      } else {
        await send(`/books/${bookId}/users/${target.userId}/block-and-revoke`);
        toast.info(t('ReaderBlockedAndRevoked'));
        setBlocked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('FailedToSubmitReport'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={!!target}
      onClose={() => {
        if (!busy) onClose();
      }}
      title={t('ReportOrBlock')}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('CancelReport')}
          </Button>
          <Button
            variant="danger"
            onClick={() => act('block')}
            aria-busy={busy}
            disabled={busy || blocked}
          >
            {blocked ? t('ReaderBlockedAndRevoked') : t('BlockAndRevokeReader')}
          </Button>
          <Button
            variant="danger"
            onClick={() => act('report')}
            aria-busy={busy}
            disabled={busy || !reason}
          >
            {t('SubmitReport')}
          </Button>
        </>
      }
    >
      <p>{t('ReportPrivacyNotice')}</p>
      <p>{target?.displayName}</p>
      {error && <Alert variant="error">{error}</Alert>}
      <label htmlFor="feedback-report-reason">{t('Reason')}</label>
      <select
        id="feedback-report-reason"
        className="br-field__input"
        value={reason}
        onChange={event => setReason(event.target.value)}
      >
        <option value="">{t('ChooseReportReason')}</option>
        {['scam', 'spam', 'harassment', 'other'].map(value => (
          <option key={value} value={value}>
            {t(`ReportReason_${value}`)}
          </option>
        ))}
      </select>
      <label htmlFor="feedback-report-details">{t('OptionalDetails')}</label>
      <textarea
        id="feedback-report-details"
        className="br-field__input"
        maxLength={1000}
        value={details}
        onChange={event => setDetails(event.target.value)}
      />
    </Dialog>
  );
};

export default FeedbackReportModal;
